<template>
  <v-text-field
    v-model="cardNumber"
    :label="t.fieldLabel"
    :mask="cardMask"
    :rules="isValidNumber"
    :placeholder="t.placeholder"
    @focus="initValidatorRules()"
    @blur="validateLuhn()"
    @input="updatedCardNumber"
    validate-on-blur
    class="flex-column"
    :disabled="fieldDisabled"
  >
    <template v-slot:append-outer>
      <img
        class="p-icon"
        :class="{ 'display-none': currentCard !== card.id }"
        v-for="(card, index) in ccOptions"
        :key="index"
        :src="getCardIcon(card.id)"
        :alt="card.localizedName"
      />
    </template>
  </v-text-field>
</template>

<script>
import { VTextField } from "vuetify/lib";
import NsIcon from "../../icons/NsIcon.vue";
import LuhnCheck from "./luhnCheck.js";

export default {
  name: "NsCreditCard",
  components: {
    VTextField,
    NsIcon
  },
  props: {
    /**
     * Available Payments for the market
     */
    availablePayments: {
      type: Array,
      value: []
    },
    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          fieldLabel: "Credit Card Number",
          fieldRequired: "Required",
          notRecognizedCard: "Not a recognized credit card",
          placeholder: "    -    -    -    "
        };
      }
    },
    /**
     * Field Disabled (for edit mode)
     */
    fieldDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cardNumber: "",
      mask: "",
      isValidNumber: [v => !!v || this.t.fieldRequired],
      currentCard: ""
    };
  },
  computed: {
    t() {
      return this.translations;
    },
    ccOptions() {
      if (this.availablePayments && this.availablePayments.length > 0) {
        return this.availablePayments.filter(card => {
          return card.formName !== "emptyPayment" ? card : null;
        });
      }      
    },
    cardMask() {
      return this.setMask();
    }
  },
  methods: {
    setMask() {
      let mask = "";

      if (this.cardNumber.match(/^4580/)) {
        // ISRA
        this.currentCard = 15;
        mask = "####-####-####-####";
      } else if (this.cardNumber.match(/^4/)) {
        // VISA
        this.currentCard = 0;
        mask = "####-####-####-####";
      } else if (this.cardNumber.match(/^51|^52|^53|^54|^55|^2/)) {
        // Mastercard
        this.currentCard = 1;
        mask = "####-####-####-####";
      } else if (this.cardNumber.match(/^6/)) {
        // Discover
        this.currentCard = 2;
        mask = "####-####-####-####";
      } else if (this.cardNumber.match(/^35/)) {
        // JCB
        this.currentCard = 21;
        mask = "####-####-####-####";
      } else if (this.cardNumber.match(/^3[47]/)) {
        // AMEX
        this.currentCard = 3;
        mask = "####-######-#####";
      } else if (this.cardNumber.match(/^3(?:0[0-5]|[68][0-9])/)) {
        // Diners Card
        this.currentCard = 110;
        mask = "####-######-####";
      } else {
        this.currentCard = "";
        mask = "####-####-####-####";
      }

      return mask;
    },
    initValidatorRules() {
      this.isValidNumber = [v => !!v || this.t.fieldRequired];
    },
    validateLuhn() {
      let luhnResult = LuhnCheck.luhnCheck(this.cardNumber, this.currentCard);

      this.isValidNumber = [
        v => !!v || this.t.fieldRequired,
        v => luhnResult || this.t.notRecognizedCard
      ];
    },
    getCardIcon(id) {
      return `https://www.nuskin.com/content/dam/apps/checkout/creditcards/CC-${id}.png`;
    },
    updatedCardNumber() {
      this.$emit('cardNumberUpdated', this.cardNumber);
      this.$emit('paymentTypeId', this.currentCard);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

::v-deep * {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.v-text-field {
  position: relative;
}

::v-deep .v-input__append-inner {
  top: auto;
  right: 5px;
  bottom: 7px;
}

::v-deep .v-input__append-outer {
  position: absolute;
  right: 0;
  margin: 0 0 0 5px;
  max-height: 20px;
}

.icon {
  color: map.get(colors.$baseColors, blackhole) !important;
}

.p-icon {
  margin-left: 5px;
  transition: filter 0.5s ease-in-out;
  width: 32px;

  &.display-none {
    display: none;
  }
}
</style>
